/**
 * DAG Sync WebSocket Management with Jotai
 * 
 * Provides a centralized WebSocket connection for DAG synchronization
 * using Jotai atoms for state management
 */

import { atom, useAtom } from 'jotai';
import { useCallback, useRef, useEffect } from 'react';
import { organisationChanged, socketConnectedSteadily } from '../dag-sync/organisation-change';
import { noteProgressSocket } from '../commit-progress';

// WebSocket connection atom
export const dagSyncSocketAtom = atom<WebSocket | null>(null);

// Connection state atom
export const dagSyncConnectionStateAtom = atom<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');

// Message handler atom - allows components to register message handlers
export const dagSyncMessageHandlerAtom = atom<((data: any) => void) | null>(null);

// Reconnection configuration
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Progressive delays in ms

/** The wait before reconnect attempt `attempt` (0-based). It NEVER runs out:
 *  after the progressive delays it stays at the last one for good. A cap on
 *  attempts left every open tab permanently stale after an outage longer
 *  than the delays added up (a deploy, a backend restart), with nothing on
 *  screen to say so (VC-e2e's restart cells, 2026-10-07). */
export function reconnectDelayMs(attempt: number): number {
  return RECONNECT_DELAYS[Math.min(Math.max(attempt, 0), RECONNECT_DELAYS.length - 1)];
}

// Circuit breaker to prevent connection loops
const CONNECTION_LOOP_THRESHOLD = 10; // Max connections allowed in time window
const CONNECTION_LOOP_WINDOW = 10000; // Time window in ms (10 seconds)

// Visibility-based disconnect configuration. DISABLED: keeping an idle
// connection alive is cheap (~1 byte/sec for pings), and the user gets an
// instant resume when returning to the tab. A re-open is no longer a full
// rebuild — each island's assembled state is kept in IndexedDB at a cursor
// and caught up through the tail (data/island-store, client caching stage 2)
// — so turning this on is now a choice about idle sockets, not a cost.
const DISCONNECT_ON_VISIBILITY_HIDE = false;

// Derived atom for connection status
export const isDagSyncConnectedAtom = atom((get) => {
  const socket = get(dagSyncSocketAtom);
  const state = get(dagSyncConnectionStateAtom);
  return socket?.readyState === WebSocket.OPEN && state === 'connected';
});

// Track how many components are using this hook (for debugging)
let hookInstanceCount = 0;

// CRITICAL: Reconnection state must be global (not per-hook-instance)
// because layout remounts during auth redirects, but timeouts persist in event loop
let globalReconnectTimeoutId: NodeJS.Timeout | null = null;
let globalReconnectAttempt = 0;

/**
 * Hook for managing DAG sync WebSocket connection with automatic reconnection
 */
export function useDagSyncSocket() {
  const [socket, setSocket] = useAtom(dagSyncSocketAtom);
  const [connectionState, setConnectionState] = useAtom(dagSyncConnectionStateAtom);
  const [messageHandler, setMessageHandler] = useAtom(dagSyncMessageHandlerAtom);
  const [isConnected] = useAtom(isDagSyncConnectedAtom);

  // Use refs for values that need to persist across renders but aren't global
  const messageHandlerRef = useRef(messageHandler);
  const intentionalDisconnectRef = useRef(false);
  const onMessageCallbackRef = useRef<((data: any) => void) | undefined>(undefined);
  const connectRef = useRef<((onMessage?: (data: any) => void) => WebSocket | null) | null>(null);

  // Circuit breaker: track connection attempts to detect loops
  const connectionAttemptsRef = useRef<number[]>([]);
  const circuitBrokenRef = useRef(false);

  // Prevent concurrent connection attempts
  const isConnectingRef = useRef(false);

  // Track if we've ever connected (to distinguish initial mount from reconnection)
  const hasEverConnectedRef = useRef(false);

  // Track hook instances (silent - only for internal debugging)
  useEffect(() => {
    hookInstanceCount++;
    return () => {
      hookInstanceCount--;
    };
  }, []);

  // Keep message handler ref in sync
  useEffect(() => {
    messageHandlerRef.current = messageHandler;
  }, [messageHandler]);

  // Store socket in ref to avoid recreating visibility listener on every socket change
  const socketRef = useRef(socket);
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  // Handle page visibility changes - reconnect when tab becomes visible
  // IMPORTANT: This effect only runs ONCE on mount to avoid event listener leaks
  useEffect(() => {
    const handleVisibilityChange = () => {
      const currentSocket = socketRef.current;

      if (document.visibilityState === 'visible') {
        // Tab became visible - check if connection is still alive or reconnect if needed
        if (!currentSocket || currentSocket.readyState !== WebSocket.OPEN) {
          // No socket or socket not open - reconnect if not intentionally disconnected
          if (!intentionalDisconnectRef.current) {
            // Clear state first
            setConnectionState('disconnected');
            setSocket(null);
            globalReconnectAttempt = 0; // Reset attempts for fresh start

            // Directly call connect for immediate reconnection
            if (connectRef.current) {
              connectRef.current(onMessageCallbackRef.current);
            }
          }
        } else if (currentSocket && currentSocket.readyState === WebSocket.OPEN) {
          // Send a test message to verify connection is alive
          try {
            currentSocket.send(JSON.stringify({ type: 'ping' }));
          } catch (e) {
            setConnectionState('disconnected');
            setSocket(null);
            globalReconnectAttempt = 0;

            // Directly call connect for immediate reconnection
            if (connectRef.current) {
              connectRef.current(onMessageCallbackRef.current);
            }
          }
        }
      } else if (document.visibilityState === 'hidden') {
        // Tab became hidden - cancel any pending reconnection attempts (use global timeout)
        if (globalReconnectTimeoutId) {
          clearTimeout(globalReconnectTimeoutId);
          globalReconnectTimeoutId = null;
        }

        // Optionally disconnect to save server resources (currently disabled)
        if (DISCONNECT_ON_VISIBILITY_HIDE) {
          // CURRENTLY DISABLED - see DISCONNECT_ON_VISIBILITY_HIDE comment above
          //
          // This would save server resources but requires:
          // - Client-side snapshot caching to avoid UX lag on return
          // - Server support for delta-only sync to avoid re-sending full snapshots
          if (currentSocket && currentSocket.readyState === WebSocket.OPEN) {
            // Mark as intentional so auto-reconnect doesn't trigger
            intentionalDisconnectRef.current = true;
            currentSocket.close(1000, 'Tab hidden'); // Normal closure
            setSocket(null);
            setConnectionState('disconnected');
            // Reset flag after a delay
            setTimeout(() => {
              intentionalDisconnectRef.current = false;
            }, 1000);
          }
        }
      }
    };

    // the network came back: try at once rather than wait out the back-off
    const handleOnline = () => {
      const currentSocket = socketRef.current;
      if (intentionalDisconnectRef.current) return;
      if (currentSocket && currentSocket.readyState === WebSocket.OPEN) return;
      if (globalReconnectTimeoutId) {
        clearTimeout(globalReconnectTimeoutId);
        globalReconnectTimeoutId = null;
      }
      globalReconnectAttempt = 0;
      connectRef.current?.(onMessageCallbackRef.current);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [setSocket, setConnectionState]); // Only depend on setters, not socket itself

  const connect = useCallback((onMessage?: (data: any) => void) => {
    // Store the callback for visibility change reconnects
    if (onMessage) {
      onMessageCallbackRef.current = onMessage;
    }

    // Check current socket state using ref to get most up-to-date value
    const currentSocket = socketRef.current;

    if (currentSocket?.readyState === WebSocket.OPEN) {
      return currentSocket; // Already connected
    }

    // CRITICAL: Prevent concurrent connection attempts using ref (more reliable than state)
    if (isConnectingRef.current) {
      return currentSocket;
    }

    if (connectionState === 'connecting') {
      return currentSocket; // Connection in progress
    }

    // Circuit breaker: detect connection loops
    const now = Date.now();
    connectionAttemptsRef.current = connectionAttemptsRef.current.filter(
      timestamp => now - timestamp < CONNECTION_LOOP_WINDOW
    );
    connectionAttemptsRef.current.push(now);

    if (connectionAttemptsRef.current.length > CONNECTION_LOOP_THRESHOLD) {
      if (!circuitBrokenRef.current) {
        circuitBrokenRef.current = true;
        console.error(
          `🚨 CIRCUIT BREAKER TRIGGERED: ${connectionAttemptsRef.current.length} connection attempts in ${CONNECTION_LOOP_WINDOW}ms. ` +
          'This indicates a connection loop bug. Pausing new connections, then trying again.'
        );
        setConnectionState('error');
        // A PAUSE, not a stop: try again once the window has passed. Stopping
        // for good left the tab stale until someone refreshed it.
        if (globalReconnectTimeoutId) clearTimeout(globalReconnectTimeoutId);
        globalReconnectTimeoutId = setTimeout(() => {
          globalReconnectTimeoutId = null;
          circuitBrokenRef.current = false;
          connectionAttemptsRef.current = [];
          connectRef.current?.(onMessageCallbackRef.current);
        }, reconnectDelayMs(RECONNECT_DELAYS.length));
      }
      return null;
    }

    // Close any existing socket that's not OPEN or CLOSED
    if (currentSocket && currentSocket.readyState !== WebSocket.CLOSED && currentSocket.readyState !== WebSocket.OPEN) {
      try {
        currentSocket.close();
      } catch (e) {
        // Ignore close errors on stale sockets
      }
    }

    const socketId = `WS_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Mark as connecting BEFORE any async operations
    isConnectingRef.current = true;
    setConnectionState('connecting');

    // Create WebSocket connection directly
    const wsUrl = process.env.NEXT_PUBLIC_SOURCEPILOT_WS_URL;
    if (!wsUrl) {
      console.error('NEXT_PUBLIC_SOURCEPILOT_WS_URL environment variable is required');
      setConnectionState('error');
      isConnectingRef.current = false;
      return null;
    }

    const newSocket = new WebSocket(`${wsUrl}/dag_sync_socket`);
    // the 4005 count goes once this socket has stayed connected a while
    let steady: (() => void) | null = null;
    (newSocket as any)._socketId = socketId; // Add ID for tracking

    newSocket.onopen = () => {
      isConnectingRef.current = false; // Clear connecting flag
      hasEverConnectedRef.current = true; // Mark that we've successfully connected at least once
      setConnectionState('connected');
      globalReconnectAttempt = 0; // Reset reconnection attempts on successful connection
      steady = socketConnectedSteadily();
      noteProgressSocket(true); // a commit's silence counts from here (item 258)
    };

    newSocket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      // Handle keepalive ping from server
      if (data.type === 'ping') {
        // Optionally respond with pong (not required, but good practice)
        if (newSocket.readyState === WebSocket.OPEN) {
          newSocket.send(JSON.stringify({ type: 'pong' }));
        }
        return; // Don't pass ping messages to application handlers
      }

      // Call the provided handler
      if (onMessage) {
        onMessage(data);
      }
      // Also call any registered global handler
      if (messageHandlerRef.current) {
        messageHandlerRef.current(data);
      }
    };

    newSocket.onclose = (event) => {
      steady?.();
      isConnectingRef.current = false; // Clear connecting flag
      noteProgressSocket(false);
      setConnectionState('disconnected');
      setSocket(null);

      // Clear any existing reconnect timeout to prevent duplicates (use global timeout)
      if (globalReconnectTimeoutId) {
        clearTimeout(globalReconnectTimeoutId);
        globalReconnectTimeoutId = null;
      }

      // 4005: the session acts as another organisation now — the page
      // reloads; no reconnect from here
      if (organisationChanged(event)) return;

      // Only attempt reconnection if not intentionally disconnected
      if (!intentionalDisconnectRef.current) {
        // Only reconnect if tab is visible (don't waste resources on background tabs)
        if (document.visibilityState === 'visible') {
          // Reconnect with back-off, for as long as it takes
          const delay = reconnectDelayMs(globalReconnectAttempt);
          globalReconnectTimeoutId = setTimeout(() => {
            globalReconnectTimeoutId = null; // Clear global ref immediately
            globalReconnectAttempt++;
            connect(onMessageCallbackRef.current);
          }, delay);
        }
        // Visibility change handler will reconnect when tab becomes visible
      } else {
        // Reset flag after intentional disconnect is processed
        intentionalDisconnectRef.current = false;
      }
    };

    newSocket.onerror = (error) => {
      console.error('DAG sync WebSocket error:', error);
      isConnectingRef.current = false; // Clear connecting flag on error too
      // Don't set to error state here - let onclose handle reconnection
    };

    setSocket(newSocket);
    return newSocket;
  }, [setSocket, setConnectionState]); // Don't depend on socket/connectionState - we use refs instead

  // Store connect function in ref for visibility handler access
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    // Clear any pending reconnection attempts (use global timeout)
    if (globalReconnectTimeoutId) {
      clearTimeout(globalReconnectTimeoutId);
      globalReconnectTimeoutId = null;
    }

    // Mark as intentional disconnect to prevent auto-reconnect
    intentionalDisconnectRef.current = true;
    globalReconnectAttempt = 0;

    if (socket) {
      socket.close();
      setSocket(null);
      setConnectionState('disconnected');
    }
  }, [socket, setSocket, setConnectionState]);

  // Cleanup on unmount - clear global timeout if this is the last hook instance
  useEffect(() => {
    return () => {
      // Only clear the global timeout if we're unmounting the last hook instance
      // This prevents clearing timeouts when layout remounts during auth redirects
      if (hookInstanceCount === 0 && globalReconnectTimeoutId) {
        clearTimeout(globalReconnectTimeoutId);
        globalReconnectTimeoutId = null;
      }
    };
  }, []);

  const sendMessage = useCallback((message: any) => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('Cannot send message - DAG sync WebSocket not connected');
      return false;
    }
  }, [socket]);

  const registerMessageHandler = useCallback((handler: (data: any) => void) => {
    setMessageHandler(() => handler);
  }, [setMessageHandler]);

  return {
    socket,
    connectionState,
    isConnected,
    hasEverConnected: hasEverConnectedRef.current,
    connect,
    disconnect,
    sendMessage,
    registerMessageHandler,
  };
}